// Roles — per-agent conversation-visibility toggles (agent_permissions), the
// email-account permission-list overlay (email_account_lists), and their
// shared audit log. Admin only (App.jsx's canAccessRoles prop, mirroring
// canAccessFlows/canAccessRuntimeFlags) — the real restriction is
// server-side (routes/admin-roles.js's requireRole('admin') and
// services/agent-visibility.js's enforcement), this page is just the control
// surface.
//
// Three views: the main Agents table (sortable, with bulk actions), Email
// Accounts (which permission-list each connected MAILBOXES address belongs
// to), and the Audit Log (agent_permissions_log, filterable).
import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Box, Stack, Typography, Button, Paper, CircularProgress, Alert, Tooltip, Switch, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,
  Select, MenuItem, Tabs, Tab, Chip, FormControl, InputLabel,
} from '@mui/material'
import {
  fetchAgentPermissions, updateAgentPermission, bulkUpdateAgentPermissions,
  fetchEmailAccountLists, updateEmailAccountList, fetchAgentPermissionsLog,
  runEntraAgentSync,
} from '../../api.js'
import {
  roleLabel, sortAgents, selectAllAgentIds, selectAgentIdsByFilter, oppositeState, canEditAgentRow, isFormerAgent,
} from '../../rolesPageHelpers.js'

// Keyed by the agent_permissions/API field name — one source of truth for
// the table header, the bulk-action column picker, and the PATCH/POST calls.
// `description` is the header tooltip's copy (Support Permissions' is
// multi-line — one bullet per bundled capability).
const TOGGLE_COLUMNS = [
  { field: 'voice', label: 'Voice', description: 'Allows users to receive phone calls' },
  { field: 'chat', label: 'Chat', description: 'Allows users to receive chats' },
  { field: 'sms', label: 'SMS', description: 'Allows users to receive SMS' },
  { field: 'ebay', label: 'eBay', description: 'Allows users to receive contacts where the client is eBay (includes eBay messages and emails)' },
  { field: 'amazon', label: 'Amazon', description: 'Allows users to receive contacts where the client is 1A or AM Amazon' },
  { field: 'trq', label: 'TRQ', description: 'Allows users to receive contacts to TRQ email accounts, or where identified client is any TRQ client' },
  { field: 'spanishEmail', label: 'Spanish Email', description: 'Allows users to receive contacts sent to Spanish Email accounts' },
  { field: 'salesHoldCalls', label: '1A Sales Hold Calls', description: 'Allows users to receive email notifications to make OB sales hold calls' },
  { field: 'supportEmail', label: 'Support Email', description: 'Allows users to receive contacts sent to Support Email accounts' },
  { field: 'supportVoice', label: 'Support Voice', description: 'Allows users to receive phone calls to Support' },
  // Tasks, Phase 1 (2026-10-13): the only way a csr gets Tasks.
  { field: 'tasks', label: 'Tasks', description: 'Can see the Tasks page and see and handle tasks in the Queue.' },
  {
    field: 'supportPermissions',
    label: 'Support Permissions',
    description: 'See Pending & Escalated contacts\nReview Spam\nTake Over contacts\nSee Spam Rules, Feedback, Templates, Permissions, Failures\nAdjust permissions for csrs and themselves (not admins or other Support Permissions holders)',
  },
]

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) + ' ET'
}

function agentDisplay(agent) {
  return agent.displayName || agent.username || agent.agentId
}

// One toggle switch for one agent/column. Admin rows still show the
// switch (per product decision) but disabled/greyed — admins always
// bypass every toggle server-side, so changing one here would be a no-op
// that misleads whoever set it.
// `editing` false = the row is locked (read-only view of what the agent has);
// the switch renders disabled until the row's Edit button is pressed.
function ToggleCell({ agent, field, busy, elevated, editing, onToggle }) {
  const switchEl = (
    <Switch
      size="small"
      checked={Boolean(agent[field])}
      disabled={busy || elevated || !editing}
      onChange={(e) => onToggle(agent, field, e.target.checked)}
      inputProps={{ 'aria-label': `${field} for ${agentDisplay(agent)}` }}
    />
  )
  const tip = elevated
    ? `${roleLabel(agent.lastKnownRole)} always sees everything, regardless of this toggle`
    : !editing ? 'Press Edit on this row to change it' : null
  return (
    <TableCell>
      {tip ? <Tooltip title={tip}><span>{switchEl}</span></Tooltip> : switchEl}
    </TableCell>
  )
}

function BulkActionsBar({ agents, selected, onSelect, onBulkApply, busy, noteText, onNoteChange }) {
  const [column, setColumn] = useState(TOGGLE_COLUMNS[0].field)
  // roleFilter is the REAL value passed to selectAgentIdsByFilter (null means
  // "Any role") — the Select itself displays '' for that option (MUI Select
  // needs a string-typed value), converted back to null on change.
  // columnFilter uses 'any' as its own sentinel instead (kept as a plain
  // string throughout, converted to null only at the call site below) — two
  // different conventions for the same "no filter" idea, matched to whichever
  // was simpler at each call site rather than forced into one pattern.
  const [roleFilter, setRoleFilter] = useState(null)
  const [columnFilter, setColumnFilter] = useState('any')
  const [stateFilter, setStateFilter] = useState('enabled')

  const count = selected.size
  const selectedIds = useMemo(() => [...selected], [selected])

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Checkboxes below hand-pick individual agents; the row here selects a whole group by criteria instead.
      </Typography>
      <TextField
        fullWidth
        size="small"
        label="Note (optional)"
        value={noteText}
        onChange={(e) => onNoteChange(e.target.value)}
        helperText="Applies to your next change, then clears"
        sx={{ mb: 1.5 }}
      />
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={600}>Select:</Typography>
        <Button size="small" onClick={() => onSelect(new Set(selectAllAgentIds(agents)))}>All ({agents.length})</Button>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">agents where role is</Typography>
          <Select size="small" value={roleFilter ?? ''} onChange={(e) => setRoleFilter(e.target.value === '' ? null : e.target.value)} inputProps={{ 'aria-label': 'Select agents by role' }}>
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="csr">csr</MenuItem>
          </Select>
          <Typography variant="body2" color="text.secondary">and</Typography>
          <Select size="small" value={columnFilter} onChange={(e) => setColumnFilter(e.target.value)} inputProps={{ 'aria-label': 'Select agents by toggle column' }}>
            <MenuItem value="any">Any toggle</MenuItem>
            {TOGGLE_COLUMNS.map((c) => <MenuItem key={c.field} value={c.field}>{c.label}</MenuItem>)}
          </Select>
          <Typography variant="body2" color="text.secondary">is</Typography>
          <Select size="small" value={stateFilter} disabled={columnFilter === 'any'} onChange={(e) => setStateFilter(e.target.value)} inputProps={{ 'aria-label': 'Select agents by toggle state' }}>
            <MenuItem value="enabled">enabled</MenuItem>
            <MenuItem value="disabled">disabled</MenuItem>
          </Select>
          <Button
            size="small"
            onClick={() => {
              onSelect(new Set(selectAgentIdsByFilter(agents, {
                role: roleFilter,
                field: columnFilter === 'any' ? null : columnFilter,
                state: columnFilter === 'any' ? null : stateFilter === 'enabled',
              })))
              // Follow the filter's own toggle column into the bulk-apply
              // picker below — "Any toggle" has nothing sensible to default
              // it to, so column is left untouched in that case.
              if (columnFilter !== 'any') setColumn(columnFilter)
            }}
          >
            Select
          </Button>
        </Stack>
        <Button size="small" color="inherit" onClick={() => onSelect(new Set())} disabled={count === 0}>Clear</Button>
      </Stack>
      {count > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="primary" label={`${count} selected`} />
          <Select size="small" value={column} onChange={(e) => setColumn(e.target.value)} inputProps={{ 'aria-label': 'Bulk-apply toggle column' }}>
            {TOGGLE_COLUMNS.map((c) => <MenuItem key={c.field} value={c.field}>{c.label}</MenuItem>)}
          </Select>
          <Button size="small" variant="contained" disabled={busy} onClick={() => onBulkApply(selectedIds, column, true)}>Turn ON</Button>
          <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => onBulkApply(selectedIds, column, false)}>Turn OFF</Button>
          <Button
            size="small"
            disabled={busy || oppositeState(agents, selectedIds, column) === null}
            onClick={() => onBulkApply(selectedIds, column, oppositeState(agents, selectedIds, column))}
          >
            Flip current state
          </Button>
        </Stack>
      )}
    </Paper>
  )
}

function ConfirmBulkDialog({ pending, saving, error, onConfirm, onCancel }) {
  if (!pending) return null
  const { agentIds, field, value, note } = pending
  const label = TOGGLE_COLUMNS.find((c) => c.field === field)?.label ?? field
  return (
    <Dialog open onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>Turn {label} {value ? 'ON' : 'OFF'} for {agentIds.length} agent{agentIds.length === 1 ? '' : 's'}?</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <DialogContentText>
          This changes every selected agent's own {label} toggle. Admin rows in the selection are unaffected (they always bypass every toggle).
        </DialogContentText>
        {note && <DialogContentText sx={{ mt: 1 }}>Note: {note}</DialogContentText>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm} disabled={saving} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {saving ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// The Entra sync's status line + "Refresh from Entra" button. The sync is
// what makes a person appear here BEFORE their first login (server/src/
// services/entra-agent-sync.js); when it is off, the list only ever fills in
// as people sign in, and this line says so and names the flag.
function EntraSyncBar({ sync, onRefresh, refreshing }) {
  if (!sync) return null
  let text
  if (!sync.enabled) text = 'Entra sync is off (ENTRA_AGENT_SYNC_ENABLED) — agents appear here after their first sign-in.'
  else if (sync.lastError) text = `Last Entra sync failed ${formatDate(sync.lastRunAt)}: ${sync.lastError}`
  else if (sync.lastSuccessAt) {
    const c = sync.lastCounts
    text = `Synced from Entra ${formatDate(sync.lastSuccessAt)}` + (c ? ` — ${c.seen} in role groups, ${c.created} new, ${c.deactivated} removed` : '')
  } else text = 'Entra sync is on but has not run yet.'
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
      <Typography variant="caption" color={sync.lastError ? 'error' : 'text.secondary'}>{text}</Typography>
      {sync.lastWarning && <Typography variant="caption" color="warning.main">{sync.lastWarning}</Typography>}
      {sync.enabled && (
        <Button size="small" onClick={onRefresh} disabled={refreshing || sync.running} startIcon={refreshing ? <CircularProgress size={12} color="inherit" /> : null}>
          Refresh from Entra
        </Button>
      )}
    </Stack>
  )
}

// `viewer` defaults to fully-elevated: display-only graying (below) never
// engages unless a real, non-elevated viewer object is actually passed in —
// the server is the real enforcement either way.
function AgentsTable({ viewer = { agentId: null, supportPermissions: false, elevated: true } }) {
  const [agents, setAgents] = useState([])
  const [sync, setSync] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Default: Role (csr, Admin), then alphabetical by first name within
  // each role — ROLE_ORDER (rolesPageHelpers.js) ranks admin/csr as
  // 0/1, so descending puts csr first. Clicking the Role header still
  // toggles to the ascending "privilege hierarchy" order (admin-first) same
  // as before; only this initial default changed.
  const [sortColumn, setSortColumn] = useState('role')
  const [sortDirection, setSortDirection] = useState('desc')
  const [selected, setSelected] = useState(new Set())
  const [busyIds, setBusyIds] = useState(new Set())
  const [rowError, setRowError] = useState(null)
  const [pendingBulk, setPendingBulk] = useState(null)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [bulkError, setBulkError] = useState(null)
  const [editingIds, setEditingIds] = useState(new Set())
  // Shared optional note — read by both toggleOne (single-row) and
  // confirmBulk (bulk), so it lives here rather than inside BulkActionsBar.
  // Stages for the NEXT change (single or bulk) and clears itself once that
  // change succeeds; left untouched on failure so the admin doesn't have to
  // retype it after a failed attempt.
  const [noteText, setNoteText] = useState('')
  // Client-side name search — agents is already the whole fetched list (no
  // new API call). Case-insensitive substring match on the same
  // displayName -> username -> agentId fallback agentDisplay() already uses,
  // so a row with no displayName is still searchable by whatever it does show.
  const [searchQuery, setSearchQuery] = useState('')
  // Agents the sync found in no role group ("Not in Entra") are hidden by
  // default — their rows are kept for the audit log and in case they return,
  // but they are noise on a page meant for the current floor. The button
  // only renders when there is at least one to show. Per-session, not
  // persisted: the default should always be the clean list.
  const [showFormer, setShowFormer] = useState(false)

  const load = useCallback(() => {
    setLoading(true); setError(null)
    fetchAgentPermissions()
      .then(({ agents: list, sync: s }) => { setAgents(list); setSync(s); setEditingIds(new Set()) })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  // Run the sync, then reload the list so new rows show up. A sync failure
  // comes back inside the status (lastError), not as a thrown error.
  const refreshFromEntra = useCallback(async () => {
    setSyncing(true); setRowError(null)
    try {
      const s = await runEntraAgentSync()
      setSync(s)
      const { agents: list, sync: s2 } = await fetchAgentPermissions()
      setAgents(list); if (s2) setSync(s2)
    } catch (e) {
      setRowError(e.message)
    } finally {
      setSyncing(false)
    }
  }, [])

  const formerCount = useMemo(() => agents.filter(isFormerAgent).length, [agents])
  const filteredAgents = useMemo(() => {
    const current = showFormer ? agents : agents.filter((a) => !isFormerAgent(a))
    const q = searchQuery.trim().toLowerCase()
    if (!q) return current
    return current.filter((a) => agentDisplay(a).toLowerCase().includes(q))
  }, [agents, searchQuery, showFormer])

  const sorted = useMemo(() => sortAgents(filteredAgents, sortColumn, sortDirection), [filteredAgents, sortColumn, sortDirection])

  const requestSort = (column) => {
    if (column === sortColumn) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortColumn(column); setSortDirection('asc') }
  }

  const toggleOne = useCallback(async (agent, field, value) => {
    setRowError(null)
    setBusyIds((s) => new Set(s).add(agent.agentId))
    try {
      const updated = await updateAgentPermission(agent.agentId, field, value, noteText.trim() || null)
      setAgents((prev) => prev.map((a) => (a.agentId === agent.agentId ? { ...a, ...updated } : a)))
      setNoteText('')
    } catch (e) {
      setRowError(e.message)
    } finally {
      setBusyIds((s) => { const next = new Set(s); next.delete(agent.agentId); return next })
    }
  }, [noteText])

  // The table is read-only until a row's Edit button is pressed (per product
  // request: a switch was one accidental click away, and a queue grant is a
  // permission). Done locks the row again. Bulk changes still confirm in a
  // dialog. Reloading the list clears edit mode.
  const toggleEditing = (agentId) => {
    setEditingIds((prev) => {
      const next = new Set(prev)
      if (next.has(agentId)) next.delete(agentId)
      else next.add(agentId)
      return next
    })
  }

  const requestBulk = useCallback((agentIds, field, value) => {
    if (!agentIds.length || value === null) return
    setBulkError(null)
    // Snapshot noteText NOW, not read live in confirmBulk() below — so what
    // the admin saw in the confirm dialog (and what confirmBulk() actually
    // sends) can't drift if they keep typing in the Notes field while the
    // dialog is open but not yet confirmed.
    setPendingBulk({ agentIds, field, value, note: noteText.trim() || null })
  }, [noteText])

  const confirmBulk = useCallback(async () => {
    if (!pendingBulk) return
    setBulkSaving(true); setBulkError(null)
    try {
      const { agentIds, field, value, note } = pendingBulk
      const { agents: updated } = await bulkUpdateAgentPermissions(agentIds, field, value, note)
      const byId = new Map(updated.map((a) => [a.agentId, a]))
      setAgents((prev) => prev.map((a) => (byId.has(a.agentId) ? { ...a, ...byId.get(a.agentId) } : a)))
      setPendingBulk(null)
      setNoteText('')
    } catch (e) {
      setBulkError(e.message)
    } finally {
      setBulkSaving(false)
    }
  }, [pendingBulk])

  const toggleRowSelected = (agentId) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(agentId)) next.delete(agentId)
      else next.add(agentId)
      return next
    })
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
  if (error) return <Alert severity="error">{error}</Alert>

  return (
    <Box>
      <EntraSyncBar sync={sync} onRefresh={refreshFromEntra} refreshing={syncing} />
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          label="Search agents"
          placeholder="Search by name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 280 }}
        />
        {formerCount > 0 && (
          <Button size="small" variant="text" onClick={() => setShowFormer((v) => !v)}>
            {showFormer ? 'Hide former agents' : `Show former agents (${formerCount})`}
          </Button>
        )}
      </Stack>
      <BulkActionsBar agents={filteredAgents} selected={selected} onSelect={setSelected} onBulkApply={requestBulk} busy={bulkSaving} noteText={noteText} onNoteChange={setNoteText} />
      {rowError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setRowError(null)}>{rowError}</Alert>}
      {agents.length === 0 ? (
        <Typography color="text.secondary">No agents yet — this page fills in as people sign in, or from Entra when the sync is on.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={selected.size > 0 && selected.size < filteredAgents.length}
                    checked={filteredAgents.length > 0 && selected.size === filteredAgents.length}
                    onChange={(e) => setSelected(e.target.checked ? new Set(selectAllAgentIds(filteredAgents)) : new Set())}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortColumn === 'agent'} direction={sortColumn === 'agent' ? sortDirection : 'asc'} onClick={() => requestSort('agent')}>Agent</TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortColumn === 'role'} direction={sortColumn === 'role' ? sortDirection : 'asc'} onClick={() => requestSort('role')}>Role</TableSortLabel>
                </TableCell>
                {TOGGLE_COLUMNS.map((c) => (
                  <TableCell key={c.field}>
                    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{c.description}</Box>}>
                      <TableSortLabel active={sortColumn === c.field} direction={sortColumn === c.field ? sortDirection : 'asc'} onClick={() => requestSort(c.field)}>{c.label}</TableSortLabel>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((agent) => {
                const elevated = agent.lastKnownRole === 'admin'
                const busy = busyIds.has(agent.agentId)
                const editing = editingIds.has(agent.agentId)
                // Display-only mirror of the server's row-level authorization
                // (admin-roles.js's canEditTargetAgent) — a support-permission
                // viewer sees every row, but one they can't touch (another
                // an admin, or a csr who already holds Support
                // Permissions) is grayed out with no Edit button. An elevated
                // viewer is unaffected: canEditRow is always true for them.
                const canEditRow = canEditAgentRow(viewer, agent)
                return (
                  <TableRow hover key={agent.agentId} selected={selected.has(agent.agentId)} sx={canEditRow ? undefined : { opacity: 0.5 }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selected.has(agent.agentId)} onChange={() => toggleRowSelected(agent.agentId)} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2">{agent.displayName ?? '—'}</Typography>
                        {agent.lastLoginAt == null && (
                          <Tooltip title="No CXHub sign-in on record for this person (records go back to when presence tracking began). Toggles set now apply on their first sign-in.">
                            <Chip size="small" variant="outlined" label="Never signed in" />
                          </Tooltip>
                        )}
                        {agent.entraActive === false && (
                          <Tooltip title="The last Entra sync did not find this person in any CXHub role group — they can no longer sign in. Row kept for the audit log.">
                            <Chip size="small" variant="outlined" color="warning" label="Not in Entra" />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{agent.username ?? agent.oid ?? agent.agentId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant={elevated ? 'filled' : 'outlined'} color={agent.lastKnownRole === 'admin' ? 'primary' : 'default'} label={roleLabel(agent.lastKnownRole)} />
                    </TableCell>
                    {TOGGLE_COLUMNS.map((c) => (
                      <ToggleCell key={c.field} agent={agent} field={c.field} busy={busy} elevated={elevated} editing={editing} onToggle={toggleOne} />
                    ))}
                    <TableCell>
                      {!elevated && canEditRow && (
                        <Button size="small" variant={editing ? 'contained' : 'outlined'} onClick={() => toggleEditing(agent.agentId)} aria-label={`${editing ? 'Done editing' : 'Edit'} ${agentDisplay(agent)}`}>
                          {editing ? 'Done' : 'Edit'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmBulkDialog pending={pendingBulk} saving={bulkSaving} error={bulkError} onConfirm={confirmBulk} onCancel={() => { if (!bulkSaving) { setPendingBulk(null); setBulkError(null) } }} />
    </Box>
  )
}

const LIST_LABELS = { spanish: 'Spanish', trq: 'TRQ', support: 'Support' }

function EmailAccountsView() {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)
  // Default: List descending — sorting on the raw list VALUE ('spanish' <
  // 'support' < 'trq'), not the display label, is what actually puts
  // Uncategorized ('' as a sort key) at the bottom in descending order.
  const [sortColumn, setSortColumn] = useState('list')
  const [sortDirection, setSortDirection] = useState('desc')
  const [listFilter, setListFilter] = useState('all')
  const [search, setSearch] = useState('')

  const load = useCallback(() => {
    setLoading(true); setError(null)
    fetchEmailAccountLists().then(setAccounts).catch((e) => setError(e.message)).finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const changeList = useCallback(async (address, list) => {
    setBusy(address); setError(null)
    try {
      const updated = await updateEmailAccountList(address, list || null)
      setAccounts((prev) => prev.map((a) => (a.address === address ? { ...a, ...updated } : a)))
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(null)
    }
  }, [])

  const requestSort = useCallback((column) => {
    if (column === sortColumn) {
      setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }, [sortColumn])

  // Filter (list + address search) first, then sort — same order the task
  // spec calls for, and it keeps the sort comparator working over the
  // already-narrowed set rather than the full list.
  const visibleAccounts = useMemo(() => {
    const filtered = accounts.filter((a) => {
      if (listFilter !== 'all' && (a.list ?? '') !== listFilter) return false
      if (search && !a.address.toLowerCase().includes(search.trim().toLowerCase())) return false
      return true
    })
    const dir = sortDirection === 'desc' ? -1 : 1
    return [...filtered].sort((a, b) => {
      const av = sortColumn === 'list' ? (a.list ?? '') : a.address
      const bv = sortColumn === 'list' ? (b.list ?? '') : b.address
      return av.localeCompare(bv) * dir
    })
  }, [accounts, listFilter, search, sortColumn, sortDirection])

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {accounts.length === 0 ? (
        <Typography color="text.secondary">No connected email accounts.</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <TextField
              size="small" label="Search address" value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="email-accounts-list-filter-label">List</InputLabel>
              <Select
                labelId="email-accounts-list-filter-label"
                label="List"
                value={listFilter}
                onChange={(e) => setListFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="">Uncategorized</MenuItem>
                <MenuItem value="spanish">Spanish</MenuItem>
                <MenuItem value="trq">TRQ</MenuItem>
                <MenuItem value="support">Support</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {visibleAccounts.length === 0 ? (
            <Typography color="text.secondary">No email accounts match these filters.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel active={sortColumn === 'address'} direction={sortColumn === 'address' ? sortDirection : 'asc'} onClick={() => requestSort('address')}>
                        Address
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortColumn === 'list'} direction={sortColumn === 'list' ? sortDirection : 'asc'} onClick={() => requestSort('list')}>
                        List
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleAccounts.map((a) => (
                    <TableRow hover key={a.address}>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{a.address}</Typography></TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={a.list ?? ''}
                          disabled={busy === a.address}
                          onChange={(e) => changeList(a.address, e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 160 }}
                        >
                          <MenuItem value="">Uncategorized</MenuItem>
                          <MenuItem value="spanish">Spanish</MenuItem>
                          <MenuItem value="trq">TRQ</MenuItem>
                          <MenuItem value="support">Support</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  )
}

function AuditLogView() {
  const [log, setLog] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [changedBy, setChangedBy] = useState('')
  const [targetAgentId, setTargetAgentId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const load = useCallback(() => {
    setLoading(true); setError(null)
    fetchAgentPermissionsLog({ changedBy: changedBy || undefined, targetAgentId: targetAgentId || undefined, from: from || undefined, to: to || undefined })
      .then(setLog).catch((e) => setError(e.message)).finally(() => setLoading(false))
  }, [changedBy, targetAgentId, from, to])

  useEffect(() => { load() }, [load])

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <TextField size="small" label="Changed by" value={changedBy} onChange={(e) => setChangedBy(e.target.value)} />
        <TextField size="small" label="Target agent id" value={targetAgentId} onChange={(e) => setTargetAgentId(e.target.value)} />
        <TextField size="small" label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : log.length === 0 ? (
        <Typography color="text.secondary">No changes match these filters.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Changed at</TableCell>
                <TableCell>Changed by</TableCell>
                <TableCell>Target agent</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Old</TableCell>
                <TableCell>New</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {log.map((entry) => (
                <TableRow hover key={entry.id}>
                  <TableCell><Typography variant="caption">{formatDate(entry.changedAt)}</Typography></TableCell>
                  <TableCell>{entry.changedBy ?? '—'}</TableCell>
                  <TableCell>{entry.targetUserDisplay ?? entry.targetAgentId}</TableCell>
                  <TableCell><Typography variant="body2" fontFamily="monospace">{entry.fieldChanged}</Typography></TableCell>
                  <TableCell>{entry.oldValue}</TableCell>
                  <TableCell>{entry.newValue}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{entry.note ?? ''}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

// viewerAgentId/viewerSupportPermissions/viewerIsElevated come from /api/me
// (App.jsx's `me` state) — what lets AgentsTable compute, for the CURRENT
// viewer, which rows they may edit (canEditAgentRow(), rolesPageHelpers.js;
// real enforcement is server-side, this is display only). An elevated
// (admin) viewer is unaffected either way — they see and edit
// everything, exactly as before.
export default function RolesPage({ viewerAgentId = null, viewerSupportPermissions = false, viewerIsElevated = false }) {
  const [tab, setTab] = useState('agents')
  const viewer = useMemo(
    () => ({ agentId: viewerAgentId, supportPermissions: viewerSupportPermissions, elevated: viewerIsElevated }),
    [viewerAgentId, viewerSupportPermissions, viewerIsElevated]
  )
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Permissions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Per-agent conversation visibility. These toggles only ever restrict a <b>csr</b> — Admin
        always sees everything, regardless of what's set here. A contact must also still pass the
        account-level Visible-to-CSRs switch (Run-Time Flags) to be seen at all.
      </Typography>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="agents" label="Agents" />
        <Tab value="email-accounts" label="Email Accounts" />
        <Tab value="log" label="Audit Log" />
      </Tabs>
      {tab === 'agents' ? <AgentsTable viewer={viewer} /> : tab === 'email-accounts' ? <EmailAccountsView /> : <AuditLogView />}
    </Box>
  )
}
